import React from "react"
import arcadeGrug from "images/love_irish/adventure/arcade-grug.png"
import tadhgFullBody from "images/love_irish/adventure/characters/tadhg_full_body.png"
import lavenderField from "images/profile_banners/lavendar-field.png"
import moonlitForest from "images/profile_banners/moonlit-forest.png"
import mtDiabloScene from "images/profile_banners/landscape_california_mt_diablo.png"
import irelandScene from "images/profile_banners/st-patricks-day-2025@2x.png"
import type { SceneImage, SceneTheme } from "./themes"

const SCENE_IMAGES: Record<SceneImage, string> = {
  lavender: lavenderField,
  moonlit: moonlitForest,
  california: mtDiabloScene,
  ireland: irelandScene,
}

/**
 * Photographic scene background: a full-cover banner chosen by time of day,
 * plus a left-weighted scrim so the left-aligned text stays legible on either
 * a bright (lavender) or dark (moonlit) image.
 */
export const SceneBackground: React.FC<{
  theme: SceneTheme
  /** Fullscreen (TV) drops the 2000px cap so the pixel-art fills the whole screen width. */
  fullscreen?: boolean
}> = ({ theme, fullscreen }) => {
  const scrim = theme.dark
    ? "bg-gradient-to-r from-black/70 via-black/40 to-black/10"
    : "bg-gradient-to-r from-white/80 via-white/45 to-white/10"

  return (
    <div
      className="absolute inset-0"
      data-testid="scene-background"
      data-image={theme.image}
    >
      {theme.image && (
        <img
          src={SCENE_IMAGES[theme.image]}
          alt=""
          aria-hidden
          className={`h-full w-full object-cover pixel-art object-center ${
            fullscreen ? "" : "mx-auto max-w-[2000px]"
          }`}
        />
      )}
      {/*<div className={`absolute inset-0 ${scrim}`} aria-hidden />*/}
    </div>
  )
}

export type CharacterKey = "grug" | "tadhg" | "none"

const CHARACTERS: Record<
  Exclude<CharacterKey, "none">,
  { src: string; alt: string; pixel: boolean; className: string }
> = {
  grug: {
    src: arcadeGrug,
    alt: "Grug",
    pixel: true,
    className: "w-24 md:w-36",
  },
  tadhg: {
    src: tadhgFullBody,
    alt: "Tadhg",
    pixel: false,
    className: "w-32 md:w-48",
  },
}

/** The sun or moon: a soft-glowing disc drawn entirely in CSS. */
export const Celestial: React.FC<{ theme: SceneTheme }> = ({ theme }) => {
  if (theme.celestial === "none") return null
  const isMoon = theme.celestial === "moon"

  return (
    <div className={`pointer-events-none absolute ${theme.celestialPosition}`}>
      <div
        className={`absolute inset-0 rounded-full blur-3xl opacity-60 ${theme.celestialGlow}`}
        aria-hidden
      />
      <div
        className={`relative h-20 w-20 rounded-full bg-gradient-to-br shadow-xl md:h-28 md:w-28 ${theme.celestialColors}`}
        role="img"
        aria-label={isMoon ? "Moon" : "Sun"}
      >
        {isMoon && (
          <>
            <span className="absolute left-4 top-4 h-3 w-3 rounded-full bg-black/10" />
            <span className="absolute bottom-5 right-6 h-2.5 w-2.5 rounded-full bg-black/10" />
            <span className="absolute right-8 top-7 h-2 w-2 rounded-full bg-black/10" />
          </>
        )}
      </div>
    </div>
  )
}

// Deterministic star positions (percentages) so the sky doesn't jitter on
// re-render. Kept small and hand-placed rather than random.
const STARS = [
  { top: "12%", left: "8%", size: 2 },
  { top: "22%", left: "24%", size: 1 },
  { top: "9%", left: "42%", size: 2 },
  { top: "30%", left: "58%", size: 1 },
  { top: "16%", left: "72%", size: 2 },
  { top: "40%", left: "14%", size: 1 },
  { top: "48%", left: "36%", size: 1 },
  { top: "26%", left: "88%", size: 2 },
  { top: "52%", left: "68%", size: 1 },
  { top: "38%", left: "82%", size: 1 },
]

export const Starfield: React.FC = () => (
  <div className="pointer-events-none absolute inset-0" aria-hidden>
    {STARS.map((star, i) => (
      <span
        key={i}
        className="absolute animate-pulse rounded-full bg-white"
        style={{
          top: star.top,
          left: star.left,
          height: star.size,
          width: star.size,
          animationDelay: `${i * 0.3}s`,
        }}
      />
    ))}
  </div>
)

/**
 * Default illustration slot: starfield (if the theme wants it), the celestial
 * disc, and a pixel character anchored bottom-left. Swap the whole thing out by
 * passing `renderScene` to DailyScene (e.g. a Rive composition of Grug + moon).
 */
export const DefaultScene: React.FC<{
  theme: SceneTheme
  character?: CharacterKey
}> = ({ theme, character = "grug" }) => {
  const char = character !== "none" ? CHARACTERS[character] : null

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden min-h-screen">
      {theme.stars && <Starfield />}
      <Celestial theme={theme} />
      {char && (
        <img
          src={char.src}
          alt={char.alt}
          className={`absolute bottom-0 left-4 md:left-12 ${char.className} drop-shadow-lg`}
          style={char.pixel ? { imageRendering: "pixelated" } : undefined}
        />
      )}
    </div>
  )
}
